import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './GestaoCasos.css';

const GestaoCasos = () => {
  const [casos, setCasos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingAnalysis, setUpdatingAnalysis] = useState({});
  const [updatingDocs, setUpdatingDocs] = useState({});
  const [expandedCasos, setExpandedCasos] = useState({});
  const [statusMessages, setStatusMessages] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    carregarCasosRecentes();
  }, []);

  // Função helper para fazer chamadas com retry
  const chamadaComRetry = async (chamadaFn, casoId, maxTentativas = 2, nomeOperacao = 'operação') => {
    let ultimoErro = null;
    
    for (let tentativa = 1; tentativa <= maxTentativas; tentativa++) {
      try {
        setStatusMessages(prev => ({
          ...prev,
          [casoId]: `${nomeOperacao}... ${tentativa > 1 ? `(tentativa ${tentativa}/${maxTentativas})` : ''}`
        }));
        
        const resultado = await chamadaFn();
        
        setStatusMessages(prev => {
          const newStatus = { ...prev };
          delete newStatus[casoId];
          return newStatus;
        });
        
        return { sucesso: true, dados: resultado };
        
      } catch (error) {
        ultimoErro = error;
        console.error(`Erro na tentativa ${tentativa} de ${nomeOperacao}:`, error);
        
        if (error.response?.status === 500 && tentativa < maxTentativas) {
          setStatusMessages(prev => ({
            ...prev,
            [casoId]: `${nomeOperacao} falhou (erro 500), tentando novamente... (tentativa ${tentativa + 1}/${maxTentativas})`
          }));
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        
        if (error.response?.status !== 500) {
          break;
        }
      }
    }
    
    setStatusMessages(prev => {
      const newStatus = { ...prev };
      delete newStatus[casoId];
      return newStatus;
    });
    
    return { sucesso: false, erro: ultimoErro };
  };

  const carregarCasosRecentes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/casos/recentes');
      
      if (response.data.success) {
        setCasos(response.data.data);
      } else {
        setError('Erro ao carregar casos');
      }
    } catch (error) {
      console.error('Erro ao buscar casos:', error);
      setError('Erro ao carregar casos recentes');
    } finally {
      setLoading(false);
    }
  };

  const handleNovoCaso = () => {
    navigate('/gestao-casos/novo');
  };

  const handlePerguntaRapida = () => {
    navigate('/chat-with-ai');
  };

  const handleVerCaso = (casoId) => {
    navigate(`/gestao-casos/${casoId}`);
  };

  const handleAtualizarAnalise = async (casoId) => {
    try {
      setUpdatingAnalysis(prev => ({ ...prev, [casoId]: true }));
      
      const resultado = await chamadaComRetry(
        () => axios.put(`/api/casos/${casoId}/atualizar-analise`),
        casoId,
        2,
        'Atualizando Análise'
      );
      
      if (resultado.sucesso) {
        const response = resultado.dados;
        
        const messyAnaliseString = response.data.data.analise_caso;
        let finalAnaliseString = messyAnaliseString;

        try {
          let analiseLimpa = JSON.parse(messyAnaliseString);

          while (analiseLimpa && typeof analiseLimpa === 'object' && 'results' in analiseLimpa) {
            analiseLimpa = analiseLimpa.results;
          }

          const dadosPadronizados = {
            results: analiseLimpa
          };
          
          finalAnaliseString = JSON.stringify(dadosPadronizados);

        } catch(e) {
          console.error("Não foi possível limpar o JSON da análise:", e);
        }

        setCasos(prevCasos => 
          prevCasos.map(caso => 
            caso.id === casoId 
              ? { ...caso, analise_caso: finalAnaliseString }
              : caso
          )
        );

        alert('Análise atualizada com sucesso!');
      } else {
        if (resultado.erro?.response?.status === 500) {
          alert('⚠️ A análise não pôde ser atualizada devido à instabilidade da API externa. Tente novamente mais tarde.');
        } else {
          alert('Erro ao atualizar análise: ' + (resultado.erro?.response?.data?.message || resultado.erro?.message || 'Erro desconhecido'));
        }
      }
    } catch (error) {
      console.error('Erro inesperado ao atualizar análise:', error);
      alert('Erro inesperado ao atualizar análise do caso');
    } finally {
      setUpdatingAnalysis(prev => ({ ...prev, [casoId]: false }));
    }
  };

  const handleAtualizarDocumentos = async (casoId) => {
    try {
      setUpdatingDocs(prev => ({ ...prev, [casoId]: true }));
      
      const resultado = await chamadaComRetry(
        () => axios.put(`/api/casos/${casoId}/atualizar-documentos`),
        casoId,
        2,
        'Atualizando Documentos'
      );
      
      if (resultado.sucesso) {
        const response = resultado.dados;
        
        setCasos(prevCasos => 
          prevCasos.map(caso => 
            caso.id === casoId 
              ? { 
                  ...caso, 
                  documentos_sugeridos: response.data.data.documentos_sugeridos,
                  documentos_sugeridos_processados: response.data.data.documentos_sugeridos_processados 
                }
              : caso
          )
        );
        alert('Documentos atualizados com sucesso!');
      } else {
        if (resultado.erro?.response?.status === 500) {
          alert('⚠️ Os documentos não puderam ser atualizados devido à instabilidade da API externa. Tente novamente mais tarde.');
        } else {
          alert('Erro ao atualizar documentos: ' + (resultado.erro?.response?.data?.message || resultado.erro?.message || 'Erro desconhecido'));
        }
      }
    } catch (error) {
      console.error('Erro inesperado ao atualizar documentos:', error);
      alert('Erro inesperado ao atualizar documentos sugeridos');
    } finally {
      setUpdatingDocs(prev => ({ ...prev, [casoId]: false }));
    }
  };

  const toggleExpandCaso = (casoId) => {
    setExpandedCasos(prev => ({
      ...prev,
      [casoId]: !prev[casoId]
    }));
  };

  const formatarData = (dataString) => {
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };

  const formatarCodigoCaso = (caso) => {
    const data = new Date(caso.created_at);
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    const letra = String.fromCharCode(65 + (caso.id % 26));
    return `#${ano}-${mes}-${dia}${letra}`;
  };

  const processarDocumentos = (documentos) => {
    if (documentos) {
      return documentos;
    }
    return [];
  };

  const renderDocumentosSugeridos = (caso) => {
    const documentos = processarDocumentos(caso.documentos_sugeridos_processados);
    
    if (!documentos || documentos.length === 0) {
      return <div className="no-docs">Nenhum documento sugerido ainda</div>;
    }

    return (
      <div className="documentos-lista">
        {documentos.map((doc, index) => (
          <div key={index} className="documento-item">
            <strong>{doc.titulo || doc.nome || 'Documento sem título'}</strong>
            {doc.justificacao && (
              <p className="documento-justificacao">{doc.justificacao}</p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="gestao-casos-container">
      <div className="gestao-casos-header">
        <h2>⚖️ Gestão de Casos</h2>
      </div>

      <div className="gestao-casos-content">
        {/* Cards de Opções de Análise */}
        <div className="opcoes-analise">
          {/* Card 1: Análise Estratégica de Caso */}
          <div className="card-opcao card-analise-estrategica">
            <div className="card-icon">🧠</div>
            <h3>Análise Estratégica de Caso</h3>
            <p className="card-descricao">
              Utilize esta opção para uma análise aprofundada de situações complexas. 
              Descreva a narrativa do seu cliente, o contexto, os intervenientes e o conflito. 
              O sistema irá gerar uma análise completa com estratégias, fundamentação legal, riscos e alternativas.
            </p>
            
            <div className="card-uso">
              <h4>Use para:</h4>
              <ul>
                <li>Descrever um novo caso de um cliente.</li>
                <li>Analisar um conflito com várias partes e eventos.</li>
                <li>Pedir um plano de ação e próximos passos.</li>
                <li>Receber uma análise de risco e oportunidades.</li>
              </ul>
            </div>
            
            <div className="card-exemplo">
              <h4>Exemplo de como descrever:</h4>
              <p className="exemplo-texto">
                "O meu cliente, Carlos, é proprietário de uma fração. Ele terminou o relacionamento 
                com a sua namorada, Rachel, que convidou para viver em sua casa sem qualquer contrato. 
                Agora, ela recusa-se a sair. Já foi enviada notificação mas sem sucesso. O cliente não 
                quer esperar por uma ação judicial longa. Quais as suas possibilidades?"
              </p>
            </div>
            
            <button className="btn-card-acao btn-analise" onClick={handleNovoCaso}>
              <span>➕</span>
              Analisar um Novo Caso
            </button>
          </div>

          {/* Card 2: Pergunta Rápida */}
          <div className="card-opcao card-pergunta-rapida">
            <div className="card-icon">❓</div>
            <h3>Pergunta Rápida</h3>
            <p className="card-descricao">
              Utilize esta opção para obter respostas diretas e factuais a questões jurídicas específicas. 
              Ideal para quando precisa de um dado concreto, como um prazo, a interpretação de um artigo 
              ou um conceito, sem a necessidade de uma análise estratégica.
            </p>
            
            <div className="card-uso">
              <h4>Use para:</h4>
              <ul>
                <li>Consultar prazos de prescrição ou caducidade.</li>
                <li>Perguntar sobre a aplicabilidade de uma lei específica.</li>
                <li>Esclarecer o significado de um conceito ou artigo legal.</li>
                <li>Obter uma resposta objetiva sem criação de cenários.</li>
              </ul>
            </div>
            
            <div className="card-exemplo">
              <h4>Exemplo de como perguntar:</h4>
              <p className="exemplo-texto">
                "Qual o prazo de prescrição de título executivo para pagamento de rendas, ao abrigo do artigo 14.º-A do NRAU?"
              </p>
              <p className="exemplo-texto">
                "Uma adenda é a forma correta de formalizar um plano de comissões anuais para um trabalhador?"
              </p>
            </div>
            
            <button className="btn-card-acao btn-pergunta" onClick={handlePerguntaRapida}>
              <span>💬</span>
              Fazer uma Pergunta Rápida
            </button>
          </div>
        </div>

        {/* Seção de Casos Recentes */}
        <div className="casos-recentes-section">
          <h3>TODOS OS MEUS CASOS</h3>
          
          {loading ? (
            <div className="loading">Carregando casos...</div>
          ) : error ? (
            <div className="error-message">{error}</div>
          ) : casos.length === 0 ? (
            <div className="no-casos">Nenhum caso encontrado</div>
          ) : (
            <div className="casos-lista">
              {casos.map((caso) => (
                <div key={caso.id} className="caso-item-expandido">
                  <div 
                    className="caso-header"
                    onClick={() => handleVerCaso(caso.id)}
                  >
                    <span className="caso-codigo">
                      Caso {formatarCodigoCaso(caso)}
                    </span>
                    <span className="gestao-caso-nome">
                      {caso.name}
                    </span>
                    <span className="caso-data">
                      {formatarData(caso.created_at)}
                    </span>
                  </div>
                  
                  {statusMessages[caso.id] && (
                    <div className="status-message-inline">
                      <div className="spinner-small"></div>
                      {statusMessages[caso.id]}
                    </div>
                  )}
                  
                  <div className="caso-acoes">
                    <div className="status-info">
                      <span className={`status-badge ${caso.analise_caso ? 'completo' : 'pendente'}`}>
                        Análise: {caso.analise_caso ? '✓ Completa' : '○ Pendente'}
                      </span>
                      <span className={`status-badge ${caso.documentos_sugeridos ? 'completo' : 'pendente'}`}>
                        Documentos: {caso.documentos_sugeridos ? '✓ Sugeridos' : '○ Pendente'}
                      </span>
                    </div>
                    
                    <div className="botoes-acao">
                      <button 
                        className="btn-atualizar-analise"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAtualizarAnalise(caso.id);
                        }}
                        disabled={updatingAnalysis[caso.id] || statusMessages[caso.id]}
                      >
                        {updatingAnalysis[caso.id] ? 'Atualizando...' : '🔄 Atualizar Análise'}
                      </button>
                      
                      <button 
                        className="btn-atualizar-docs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAtualizarDocumentos(caso.id);
                        }}
                        disabled={updatingDocs[caso.id] || statusMessages[caso.id]}
                      >
                        {updatingDocs[caso.id] ? 'Atualizando...' : '📄 Atualizar Documentos'}
                      </button>
                      
                      {caso.documentos_sugeridos && (
                        <button 
                          className="btn-ver-docs"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandCaso(caso.id);
                          }}
                        >
                          {expandedCasos[caso.id] ? '▼ Ocultar' : '▶ Ver Documentos'}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {expandedCasos[caso.id] && caso.documentos_sugeridos && (
                    <div className="caso-documentos-expandido">
                      <h4>Documentos Sugeridos:</h4>
                      {renderDocumentosSugeridos(caso)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GestaoCasos;